/* Custom journey timeline — scroll-driven reveal, line fill, marker activation */
(function () {
  if (customElements.get('journey-timeline')) return;

  class JourneyTimeline extends HTMLElement {
    connectedCallback() {
      this.fill = this.querySelector('[data-line-fill]');
      this.stages = Array.from(this.querySelectorAll('[data-stage]'));
      this.section = this.closest('.custom-journey');
      this.animateLine = this.dataset.animateLine === 'true';
      this.layout = this.dataset.layout || 'split';
      this.isHorizontal = this.layout === 'horizontal_scroll';

      this._setupReveal();

      if (this.isHorizontal) {
        // Horizontal mode reads its own scroll position instead of the
        // page's — activation + line fill are driven by which card sits
        // closest to the rail's centre.
        this._setupHorizontalActivation();
        if (this.animateLine) this._setupHorizontalLineFill();
        this._setupHorizontalArrows();
      } else {
        // Stage activation (pill light-up, check colours, marker colour,
        // connector colour) is decoupled from the line-fill animation so
        // users can disable the line and still get the per-stage reveal.
        this._setupStageActivation();
        if (this.animateLine) this._setupLineFill();
      }
    }

    /* === Horizontal scroll mode === */

    _setupHorizontalActivation() {
      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = this.getBoundingClientRect();
        const railCenter = rect.left + rect.width / 2;
        this.stages.forEach((stage) => {
          const r = stage.getBoundingClientRect();
          const mid = r.left + r.width / 2;
          // A card is active when its centre is at or to the left of the
          // rail centre — matches the vertical scroll behaviour (once
          // past the trigger, stays lit).
          stage.classList.toggle('is-active', mid <= railCenter + 4);
        });
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      this.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      requestAnimationFrame(update);
    }

    _setupHorizontalLineFill() {
      if (!this.fill) return;
      let ticking = false;
      const update = () => {
        ticking = false;
        const max = this.scrollWidth - this.clientWidth;
        const p = max > 0 ? this.scrollLeft / max : 1;
        this.style.setProperty('--j-line-progress', p.toFixed(4));
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      this.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      requestAnimationFrame(update);
    }

    _setupHorizontalArrows() {
      const wrap = this.parentElement;
      if (!wrap) return;
      const prev = wrap.querySelector('[data-hs-prev]');
      const next = wrap.querySelector('[data-hs-next]');
      if (!prev && !next) return;

      const step = () => {
        const first = this.stages[0];
        if (!first) return 300;
        const styles = getComputedStyle(this);
        const gap = parseInt(styles.columnGap || styles.gap, 10) || 0;
        return first.getBoundingClientRect().width + gap;
      };
      prev?.addEventListener('click', () => this.scrollBy({ left: -step(), behavior: 'smooth' }));
      next?.addEventListener('click', () => this.scrollBy({ left: step(), behavior: 'smooth' }));
    }

    /* Fade-up each stage as it enters viewport (once). Cheap, runs only
       while stages are still hidden. */
    _setupReveal() {
      const isAnimated = this.section?.classList.contains('custom-journey--animated');
      if (!isAnimated || !('IntersectionObserver' in window)) {
        this.stages.forEach((s) => s.classList.add('is-visible'));
        return;
      }

      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
      );

      this.stages.forEach((s) => observer.observe(s));
    }

    /* Vertical line "draws" with scroll progress. Progress = how far the
       viewport center has travelled through the timeline element. Updates
       a CSS variable that the fill height reads via calc(). */
    _setupLineFill() {
      if (!this.fill) return;

      let ticking = false;
      const update = () => {
        ticking = false;
        const rect = this.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        // Start filling when the timeline top is half-way up the viewport;
        // finish when its bottom hits half-way. Gives a satisfying "as you
        // reach it" feel.
        const start = vh * 0.55;
        const end = vh * 0.45;
        const total = (rect.bottom - end) - (rect.top - start);
        const elapsed = start - rect.top;
        const progress = Math.max(0, Math.min(1, elapsed / Math.max(1, rect.height - (end - start))));
        this.style.setProperty('--j-line-progress', progress.toFixed(4));
      };

      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };

      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      // Initial paint after layout settles.
      requestAnimationFrame(update);
    }

    /* Stage activation — scroll-position based so it behaves like
       Lymphoria: a stage gets .is-active the moment its top crosses the
       viewport's middle, and STAYS active once it scrolls above. Pull
       back up and the stage deactivates again when its top drops below
       the middle. IntersectionObserver was a bad fit because it
       reported 'no longer intersecting' once a stage scrolled fully
       above the viewport, which incorrectly removed the active state. */
    _setupStageActivation() {
      let ticking = false;
      const update = () => {
        ticking = false;
        const triggerY = (window.innerHeight || document.documentElement.clientHeight) * 0.5;
        this.stages.forEach((stage) => {
          const rect = stage.getBoundingClientRect();
          stage.classList.toggle('is-active', rect.top < triggerY);
        });
      };
      const onScroll = () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      // Initial paint so stages already past the trigger on page load
      // come up active.
      requestAnimationFrame(update);
    }
  }

  customElements.define('journey-timeline', JourneyTimeline);
})();
